import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { keys as groupsKeys, useAddUsersToGroupMutation } from '~/queries/user-groups'
import { useUsersListQuery } from '~/queries/users'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption } from '~/utils/yup'

const MODAL_ID = 'add-user-to-group-modal'

export interface FormValues {
  memberUsers: IOption[]
}

const validationSchema = Yup.object().shape({
  memberUsers: Yup.array().of(RequiredYupOption).required(FORM_ERRORS.REQUIRED).min(1, FORM_ERRORS.AT_LEAST_ONE),
})

interface FormProps {
  groupId: string
  onClose: VoidFunction
  onFailure: (error: unknown) => void
  onSuccess: VoidFunction
}

const AddUserToGroupForm = ({ groupId, onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: { memberUsers: [] },
    resolver: yupResolver(validationSchema),
  })

  const queryClient = useQueryClient()

  /** Queries & Mutations */

  const { data: users, isLoading: isUsersLoading } = useUsersListQuery()

  const { mutateAsync: addUsersToGroup } = useAddUsersToGroupMutation()

  const handleFormSubmit = async (values: FormValues) => {
    const { memberUsers } = values

    try {
      const userIds = memberUsers.map(({ value }) => value)
      await addUsersToGroup({ groupId, userIds })
      await queryClient.invalidateQueries({ queryKey: groupsKeys.details(groupId) })
      onSuccess()
    } catch (error) {
      onFailure(error)
    }
  }

  const options = users?.map(({ name, user_id }) => ({ label: name, value: user_id })) || []

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <Controller
            control={control}
            name="memberUsers"
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                autoFocus
                error={errors.memberUsers?.message}
                isLoading={isUsersLoading}
                label="Users"
                options={options}
                placeholder="Select users"
                required
              />
            )}
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const AddUserToGroupModal = ({ groupId, isOpen, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add users to the group</ModalHeader>
      <AddUserToGroupForm groupId={groupId} onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} />
    </Modal>
  )
}

export default AddUserToGroupModal
